import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type WhoElseDoesThisItem } from '@bliks-io/contracts';
import { Card, CardHeader, CardContent } from '@bliks-io/ui';
import { useProfile } from '@/api/queries/useProfile';
import { env } from '@/config/env';
import { WhoElseDoesThisPicker } from '@/components/entity-pickers/who-else/WhoElseDoesThisPicker';

type Props = {
  value: WhoElseDoesThisItem[];
  onChange: (value: WhoElseDoesThisItem[]) => void | Promise<void>;
  /** The step's assignee (when a User) — excluded from the picker as a duplicate. */
  assigneeUserId?: string;
  processId?: string;
  recordingId?: string;
};

/**
 * Recording-flow card chrome around the shared {@link WhoElseDoesThisPicker}.
 * Owns the heading and the exclusion list (current user outside dev + the
 * step's assignee); the picker owns the preview cell and person modal.
 */
function WhoElseDoesThisCard({ value, onChange, assigneeUserId, processId, recordingId }: Props) {
  const { t } = useTranslation();
  const { data: currentUser } = useProfile();

  const excludeIds = useMemo(() => {
    const ids: string[] = [];
    if (!env.DEV && currentUser?._id) ids.push(currentUser._id);
    if (assigneeUserId) ids.push(assigneeUserId);
    return ids;
  }, [currentUser?._id, assigneeUserId]);

  return (
    <Card className="nopan w-[760px] border-border">
      <CardHeader className="pb-4">
        <div className="text-lg font-semibold">{t('processPage.whoElseDoesThis')}</div>
        <div className="text-sm text-muted-foreground">
          <Trans i18nKey="processPage.selectColleaguesWhoAlsoPerform">
            Select colleagues who also perform this process step
          </Trans>
        </div>
      </CardHeader>
      <CardContent>
        <WhoElseDoesThisPicker
          value={value}
          onChange={onChange}
          excludeIds={excludeIds}
          processId={processId}
          recordingId={recordingId}
        />
      </CardContent>
    </Card>
  );
}

export default WhoElseDoesThisCard;
